import React, { useState, useEffect } from 'react';
import { PowerService, PowerDataResponse } from '../../../services/powerService';
import { supabase } from '../../auth/AuthProvider';
import { LoadingSpinner } from '../../common/LoadingSpinner';
import { format, sub } from 'date-fns';
import PowerDataChart from './PowerDataChart';
import PowerDataSummary from './PowerDataSummary';

interface PowerDataProps {
  defaultCountryCode?: string;
}

const timePeriods = [
  { value: 'latest', label: 'Latest (last 56 hours)' },
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
];

const PowerData: React.FC<PowerDataProps> = ({ defaultCountryCode = 'DE' }) => {
  const [countryOptions, setCountryOptions] = useState<Record<string, string> | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<string>(defaultCountryCode);
  const [selectedTimePeriod, setSelectedTimePeriod] = useState('latest');
  const [powerData, setPowerData] = useState<PowerDataResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [hasForecast, setHasForecast] = useState<boolean>(false);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const getToken = async () => {
          const { data } = await supabase.auth.getSession();
          const token = data.session?.access_token;
          if (!token) {
            throw new Error('No authentication token available');
          }
          return token;
        };

        const powerService = new PowerService(import.meta.env.VITE_API_BASE_URL, getToken);
        const countriesResponse = await powerService.getPowerDataCountries();

        if (countriesResponse.error || !countriesResponse.data) {
          throw new Error(countriesResponse.error || 'Failed to fetch countries');
        }

        setCountryOptions(countriesResponse.data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An unknown error occurred');
      }
    };

    fetchCountries();
  }, []);

  useEffect(() => {
    const fetchPowerData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const getToken = async () => {
          const { data } = await supabase.auth.getSession();
          const token = data.session?.access_token;
          if (!token) {
            throw new Error('No authentication token available');
          }
          return token;
        };

        const powerService = new PowerService(import.meta.env.VITE_API_BASE_URL, getToken);

        const params: {
          country_code: string;
          start_time?: string;
          end_time?: string;
          latest?: boolean;
        } = {
          country_code: selectedCountry,
        };

        // Handle different time period options
        if (selectedTimePeriod === 'latest') {
          params.latest = true;
        } else {
          // Calculate time period
          let startDate = new Date();
          switch (selectedTimePeriod) {
            case '7d':
              startDate = sub(new Date(), { days: 7 });
              break;
            case '30d':
              startDate = sub(new Date(), { days: 30 });
              break;
            case '90d':
              startDate = sub(new Date(), { days: 90 });
              break;
            default: // 24h
              startDate = sub(new Date(), { days: 1 });
              break;
          }

          params.start_time = format(startDate, "yyyy-MM-dd'T'HH:mm:ss'Z'");
          params.end_time = format(new Date(), "yyyy-MM-dd'T'HH:mm:ss'Z'");
        }

        const dataResponse = await powerService.getPowerData(params);

        if (dataResponse.error || !dataResponse.data) {
          throw new Error(dataResponse.error || 'Failed to fetch power data');
        }

        setPowerData(dataResponse.data);

        // Check if any data points have forecast information
        const hasForecastData = dataResponse.data.data.some((dataPoint) => {
          return (
            (dataPoint.generation_forecast !== null &&
              dataPoint.generation_forecast !== undefined) ||
            (dataPoint.solar_forecast !== null && dataPoint.solar_forecast !== undefined) ||
            (dataPoint.wind_onshore_forecast !== null &&
              dataPoint.wind_onshore_forecast !== undefined) ||
            (dataPoint.wind_offshore_forecast !== null &&
              dataPoint.wind_offshore_forecast !== undefined)
          );
        });

        setHasForecast(hasForecastData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An unknown error occurred');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPowerData();
  }, [selectedCountry, selectedTimePeriod]);

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCountry(e.target.value);
  };

  const handleTimePeriodChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedTimePeriod(e.target.value);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Power Generation and Consumption
        </h2>

        {/* Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div>
            <label
              htmlFor="country"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Country
            </label>
            <select
              id="country"
              value={selectedCountry}
              onChange={handleCountryChange}
              className="w-full p-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-gray-100"
            >
              {countryOptions &&
                Object.entries(countryOptions).map(([code, name]) => (
                  <option key={code} value={code}>
                    {name}
                  </option>
                ))}
              {!countryOptions && <option value={defaultCountryCode}>{defaultCountryCode}</option>}
            </select>
          </div>

          <div>
            <label
              htmlFor="timePeriod"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Time Period
            </label>
            <select
              id="timePeriod"
              value={selectedTimePeriod}
              onChange={handleTimePeriodChange}
              className="w-full p-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-gray-100"
            >
              {timePeriods.map((period) => (
                <option key={period.value} value={period.value}>
                  {period.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="flex justify-center py-10">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-md text-red-700 dark:text-red-400">
            <p>Error: {error}</p>
          </div>
        ) : powerData && powerData.data.length > 0 ? (
          <div className="space-y-6">
            <PowerDataSummary powerData={powerData} />
            <PowerDataChart powerData={powerData} dataView="both" hasForecast={hasForecast} />

            {hasForecast && (
              <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-md text-gray-700 dark:text-gray-300 mt-4 border border-gray-200 dark:border-gray-700">
                <p className="text-sm">
                  <span className="font-semibold">Note:</span> This dataset includes forecast data
                  alongside actual data.
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-md text-yellow-700 dark:text-yellow-400">
            <p>No data available for the selected filters. Please try a different combination.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PowerData;
